import React from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import DialogHeader from './DialogHeader'
import type { ModalVariant } from './modalVariants'
import {
  DIALOG_BODY_PX,
  DIALOG_BODY_PY,
  DIALOG_FOOTER_GAP,
  DIALOG_FOOTER_PX,
  DIALOG_FOOTER_PY,
  dialogMaxWidth,
} from '../../../theme/dialogs'
import type { DialogSize } from '../../../theme/dialogs'

export type { DialogSize }

/**
 * The shared dialog shell. Wraps MUI `Dialog` with the standard
 * `DialogHeader` (eyebrow + title + subtitle + optional leading icon /
 * adornment / close), a body slot, and a standardised footer. Header /
 * body / footer spacing comes from `theme/dialogs.ts`; the paper radius +
 * shadow come from the global `MuiDialog` override in `main.tsx`.
 *
 * Presentational only - imports nothing from `admin/` or `inspector/`, so
 * a change here can never reach one domain via the other.
 *
 * Keyboard submit: when `onSubmit` is provided, Ctrl/Cmd+Enter fires it
 * (mirrors the ExceptionDrawer note composer). Plain Enter stays a
 * newline inside multiline fields. `ConfirmDialog` wires its gated confirm
 * handler here so a keyboard user can confirm without the mouse.
 */
export interface BaseDialogProps {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  titleAttr?: string
  eyebrow?: string
  subtitle?: React.ReactNode
  /** Header icon variant - resolves to one icon + colour in a tinted badge.
   *  Defaults to `info`. Pick a variant rather than passing a raw icon. */
  variant?: ModalVariant
  /** Escape hatch: a raw icon node that replaces the variant badge. Reserved
   *  for the rare non-taxonomy header; prefer `variant`. */
  leadingIcon?: React.ReactNode
  size?: DialogSize
  /** Chips / toggles shown on the right of the header title row. */
  headerAdornment?: React.ReactNode
  /** `<DialogActions>` contents - caller owns button order / labels. */
  footer?: React.ReactNode
  /** Default true; controls backdrop-click close + the header close icon. */
  dismissable?: boolean
  /** Fires on Ctrl/Cmd+Enter when provided. */
  onSubmit?: () => void
  /** Extra sx applied to the `DialogContent` body. */
  contentSx?: SxProps<Theme>
  /** Extra sx applied to the `Dialog` root - e.g. a `zIndex` override so the
   *  dialog can layer above a custom side panel that sits at `modal + n`. */
  sx?: SxProps<Theme>
  children: React.ReactNode
}

const BaseDialog: React.FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  titleAttr,
  eyebrow,
  subtitle,
  variant = 'info',
  leadingIcon,
  size = 'sm',
  headerAdornment,
  footer,
  dismissable = true,
  onSubmit,
  contentSx,
  sx,
  children,
}) => {
  const isFullscreen = size === 'fullscreen'

  const handleClose = (_e: unknown, reason?: string) => {
    // Honour `dismissable` for backdrop / escape dismissal.
    if (!dismissable && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return
    }
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={dialogMaxWidth(size)}
      fullWidth={!isFullscreen}
      fullScreen={isFullscreen}
      onKeyDown={onSubmit ? handleKeyDown : undefined}
      sx={sx}
    >
      <DialogHeader
        eyebrow={eyebrow}
        title={title}
        titleAttr={titleAttr}
        subtitle={subtitle}
        variant={variant}
        leadingIcon={leadingIcon}
        adornment={headerAdornment}
        onClose={onClose}
        dismissable={dismissable}
      />
      <DialogContent
        dividers
        sx={{ px: DIALOG_BODY_PX, py: DIALOG_BODY_PY, ...contentSx }}
      >
        {children}
      </DialogContent>
      {footer != null && (
        <DialogActions
          sx={{
            px: DIALOG_FOOTER_PX,
            py: DIALOG_FOOTER_PY,
            gap: DIALOG_FOOTER_GAP,
            justifyContent: 'flex-end',
          }}
        >
          {footer}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default BaseDialog
